'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

type View = 'landing' | 'assessment' | 'results';

interface Question {
  id: string;
  text: string;
  criterion: string;
  level: 'A' | 'AA' | 'AAA';
  weight: number;
}

interface Section {
  id: string;
  title: string;
  principle: string;
  description: string;
  questions: Question[];
}

const sections: Section[] = [
  {
    id: 'perceivable-text',
    title: 'Text Alternatives & Media',
    principle: 'Perceivable',
    description: 'Provide text alternatives and captions for non-text content',
    questions: [
      { id: 'pt1', text: 'Do all images have appropriate alt text describing their content or function?', criterion: '1.1.1', level: 'A', weight: 10 },
      { id: 'pt2', text: 'Are decorative images marked as such (empty alt or CSS background)?', criterion: '1.1.1', level: 'A', weight: 8 },
      { id: 'pt3', text: 'Do all pre-recorded videos have synchronized captions?', criterion: '1.2.2', level: 'A', weight: 9 },
      { id: 'pt4', text: 'Do all pre-recorded videos have audio descriptions?', criterion: '1.2.5', level: 'AA', weight: 8 },
      { id: 'pt5', text: 'Do live videos have real-time captions?', criterion: '1.2.4', level: 'AA', weight: 8 },
      { id: 'pt6', text: 'Is sign language interpretation provided for pre-recorded audio?', criterion: '1.2.6', level: 'AAA', weight: 5 },
    ],
  },
  {
    id: 'perceivable-structure',
    title: 'Content Structure & Presentation',
    principle: 'Perceivable',
    description: 'Structure content logically and make it adaptable',
    questions: [
      { id: 'ps1', text: 'Is information and structure conveyed through semantic HTML (not just visual styling)?', criterion: '1.3.1', level: 'A', weight: 10 },
      { id: 'ps2', text: 'Is the reading sequence logical when CSS is disabled?', criterion: '1.3.2', level: 'A', weight: 8 },
      { id: 'ps3', text: 'Do instructions not rely solely on sensory characteristics (color, shape, position)?', criterion: '1.3.3', level: 'A', weight: 8 },
      { id: 'ps4', text: 'Does content display correctly in both portrait and landscape orientations?', criterion: '1.3.4', level: 'AA', weight: 7 },
      { id: 'ps5', text: 'Do form fields identify their purpose programmatically for autocomplete?', criterion: '1.3.5', level: 'AA', weight: 7 },
      { id: 'ps6', text: 'Can users change text spacing without loss of content or functionality?', criterion: '1.4.12', level: 'AA', weight: 7 },
    ],
  },
  {
    id: 'perceivable-visual',
    title: 'Visual Presentation',
    principle: 'Perceivable',
    description: 'Ensure content is distinguishable and readable',
    questions: [
      { id: 'pv1', text: 'Is color not the only means of conveying information?', criterion: '1.4.1', level: 'A', weight: 10 },
      { id: 'pv2', text: 'Do text and images of text have sufficient contrast (4.5:1 for normal text)?', criterion: '1.4.3', level: 'AA', weight: 10 },
      { id: 'pv3', text: 'Can text be resized up to 200% without loss of content or functionality?', criterion: '1.4.4', level: 'AA', weight: 9 },
      { id: 'pv4', text: 'Is text used instead of images of text where possible?', criterion: '1.4.5', level: 'AA', weight: 8 },
      { id: 'pv5', text: 'Do non-text elements (UI components, graphics) have 3:1 contrast?', criterion: '1.4.11', level: 'AA', weight: 9 },
      { id: 'pv6', text: 'Does content reflow without horizontal scrolling at 320px width?', criterion: '1.4.10', level: 'AA', weight: 8 },
    ],
  },
  {
    id: 'operable-keyboard',
    title: 'Keyboard Accessibility',
    principle: 'Operable',
    description: 'Make all functionality available from a keyboard',
    questions: [
      { id: 'ok1', text: 'Is all functionality operable through a keyboard interface?', criterion: '2.1.1', level: 'A', weight: 10 },
      { id: 'ok2', text: 'Is there no keyboard trap (users can navigate away from all components)?', criterion: '2.1.2', level: 'A', weight: 10 },
      { id: 'ok3', text: 'Are keyboard shortcuts that use single character keys configurable?', criterion: '2.1.4', level: 'A', weight: 7 },
      { id: 'ok4', text: 'Is focus visible when navigating with keyboard?', criterion: '2.4.7', level: 'AA', weight: 9 },
      { id: 'ok5', text: 'Does focus order follow a logical sequence?', criterion: '2.4.3', level: 'A', weight: 9 },
      { id: 'ok6', text: 'Is there a skip link to bypass repeated navigation?', criterion: '2.4.1', level: 'A', weight: 8 },
    ],
  },
  {
    id: 'operable-timing',
    title: 'Timing & Motion',
    principle: 'Operable',
    description: 'Provide enough time and control over motion',
    questions: [
      { id: 'ot1', text: 'Can time limits be turned off, adjusted, or extended?', criterion: '2.2.1', level: 'A', weight: 9 },
      { id: 'ot2', text: 'Can users pause, stop, or hide moving or auto-updating content?', criterion: '2.2.2', level: 'A', weight: 9 },
      { id: 'ot3', text: 'Is there no content that flashes more than 3 times per second?', criterion: '2.3.1', level: 'A', weight: 10 },
      { id: 'ot4', text: 'Can motion-triggered functionality be disabled or triggered alternatively?', criterion: '2.5.4', level: 'A', weight: 7 },
      { id: 'ot5', text: 'Can users disable non-essential animations?', criterion: '2.3.3', level: 'AAA', weight: 6 },
    ],
  },
  {
    id: 'operable-navigation',
    title: 'Navigation & Input',
    principle: 'Operable',
    description: 'Help users navigate and find content',
    questions: [
      { id: 'on1', text: 'Do pages have descriptive and unique titles?', criterion: '2.4.2', level: 'A', weight: 9 },
      { id: 'on2', text: 'Is the purpose of each link clear from link text or context?', criterion: '2.4.4', level: 'A', weight: 9 },
      { id: 'on3', text: 'Are there multiple ways to find pages (search, sitemap, navigation)?', criterion: '2.4.5', level: 'AA', weight: 8 },
      { id: 'on4', text: 'Are headings and labels descriptive?', criterion: '2.4.6', level: 'AA', weight: 8 },
      { id: 'on5', text: 'Are navigation mechanisms consistent across pages?', criterion: '3.2.3', level: 'AA', weight: 8 },
      { id: 'on6', text: 'Can pointer gestures be performed with a single pointer?', criterion: '2.5.1', level: 'A', weight: 8 },
      { id: 'on7', text: 'Can drag operations be achieved with single pointer alternatives?', criterion: '2.5.7', level: 'AA', weight: 7 },
      { id: 'on8', text: 'Is the target size for pointer inputs at least 24x24 CSS pixels?', criterion: '2.5.8', level: 'AA', weight: 8 },
    ],
  },
  {
    id: 'understandable-content',
    title: 'Readable Content',
    principle: 'Understandable',
    description: 'Make text content readable and understandable',
    questions: [
      { id: 'uc1', text: 'Is the language of the page programmatically identified?', criterion: '3.1.1', level: 'A', weight: 9 },
      { id: 'uc2', text: 'Are language changes in content programmatically identified?', criterion: '3.1.2', level: 'AA', weight: 7 },
      { id: 'uc3', text: 'Are unusual words and jargon defined or explained?', criterion: '3.1.3', level: 'AAA', weight: 5 },
      { id: 'uc4', text: 'Are abbreviations expanded or defined?', criterion: '3.1.4', level: 'AAA', weight: 5 },
    ],
  },
  {
    id: 'understandable-input',
    title: 'Input Assistance',
    principle: 'Understandable',
    description: 'Help users avoid and correct mistakes',
    questions: [
      { id: 'ui1', text: 'Are input errors identified and described in text?', criterion: '3.3.1', level: 'A', weight: 10 },
      { id: 'ui2', text: 'Are form labels or instructions provided?', criterion: '3.3.2', level: 'A', weight: 10 },
      { id: 'ui3', text: 'Are error suggestions provided when possible?', criterion: '3.3.3', level: 'AA', weight: 9 },
      { id: 'ui4', text: 'Can legal/financial submissions be reviewed, confirmed, or reversed?', criterion: '3.3.4', level: 'AA', weight: 9 },
      { id: 'ui5', text: 'Is redundant entry avoided (don&apos;t ask for same info twice)?', criterion: '3.3.7', level: 'A', weight: 7 },
      { id: 'ui6', text: 'Is accessible authentication available (no cognitive function test)?', criterion: '3.3.8', level: 'AA', weight: 8 },
    ],
  },
  {
    id: 'robust',
    title: 'Robust & Compatible',
    principle: 'Robust',
    description: 'Maximize compatibility with assistive technologies',
    questions: [
      { id: 'rb1', text: 'Is HTML well-formed with complete start/end tags and proper nesting?', criterion: '4.1.1', level: 'A', weight: 9 },
      { id: 'rb2', text: 'Do custom components have appropriate name, role, and value?', criterion: '4.1.2', level: 'A', weight: 10 },
      { id: 'rb3', text: 'Are status messages conveyed to assistive technologies without focus?', criterion: '4.1.3', level: 'AA', weight: 8 },
      { id: 'rb4', text: 'Do you test with screen readers (NVDA, VoiceOver, JAWS)?', criterion: 'Best Practice', level: 'AA', weight: 9 },
      { id: 'rb5', text: 'Do you include users with disabilities in usability testing?', criterion: 'Best Practice', level: 'AA', weight: 8 },
    ],
  },
];

export default function WCAGCompliance() {
  const [view, setView] = useState<View>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, 'yes' | 'no' | 'partial' | 'na'>>({});
  const [targetLevel, setTargetLevel] = useState<'A' | 'AA' | 'AAA'>('AA');
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'wcag-compliance',
    toolCategory: 'compliance',
  });

  const handleAnswer = (questionId: string, value: 'yes' | 'no' | 'partial' | 'na') => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const getApplicableQuestions = () => {
    const levelOrder = { A: 1, AA: 2, AAA: 3 };
    return sections.flatMap((s) =>
      s.questions.filter((q) => levelOrder[q.level] <= levelOrder[targetLevel])
    );
  };

  const calculateScore = () => {
    let earned = 0;
    let possible = 0;
    const gaps: { criterion: string; question: string; status: string; level: string }[] = [];

    const levelOrder = { A: 1, AA: 2, AAA: 3 };

    sections.forEach((section) => {
      section.questions.forEach((q) => {
        if (levelOrder[q.level] > levelOrder[targetLevel]) return;

        const answer = answers[q.id];
        if (answer === 'na') return;

        possible += q.weight;
        if (answer === 'yes') {
          earned += q.weight;
        } else if (answer === 'partial') {
          earned += q.weight * 0.5;
          gaps.push({ criterion: q.criterion, question: q.text, status: 'Partial', level: q.level });
        } else if (answer === 'no') {
          gaps.push({ criterion: q.criterion, question: q.text, status: 'Fail', level: q.level });
        }
      });
    });

    return {
      score: possible > 0 ? Math.round((earned / possible) * 100) : 0,
      gaps: gaps.sort((a, b) => {
        const levelOrder = { A: 1, AA: 2, AAA: 3 };
        return levelOrder[a.level as keyof typeof levelOrder] - levelOrder[b.level as keyof typeof levelOrder];
      }),
    };
  };

  const getSectionScore = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return 0;

    const levelOrder = { A: 1, AA: 2, AAA: 3 };
    let earned = 0;
    let possible = 0;

    section.questions.forEach((q) => {
      if (levelOrder[q.level] > levelOrder[targetLevel]) return;

      const answer = answers[q.id];
      if (answer === 'na') return;
      possible += q.weight;
      if (answer === 'yes') earned += q.weight;
      else if (answer === 'partial') earned += q.weight * 0.5;
    });

    return possible > 0 ? Math.round((earned / possible) * 100) : 0;
  };

  const getComplianceLevel = (score: number) => {
    if (score >= 95) return { level: 'Conformant', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (score >= 80) return { level: 'Substantially Conformant', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (score >= 60) return { level: 'Partially Conformant', color: 'text-accent-400', bg: 'bg-accent-500/20' };
    return { level: 'Not Conformant', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const exportToMarkdown = () => {
    const { score, gaps } = calculateScore();
    const compliance = getComplianceLevel(score);

    let md = `# WCAG 2.2 Accessibility Assessment Report\n\n`;
    md += `**Generated:** ${new Date().toLocaleDateString()}\n`;
    md += `**Target Level:** WCAG 2.2 Level ${targetLevel}\n\n`;
    md += `## Overall Conformance Score\n\n`;
    md += `**Score:** ${score}%\n`;
    md += `**Status:** ${compliance.level}\n\n`;

    md += `## POUR Principle Scores\n\n`;
    md += `| Principle | Score |\n`;
    md += `|-----------|-------|\n`;

    const principles = ['Perceivable', 'Operable', 'Understandable', 'Robust'];
    principles.forEach((principle) => {
      const principleSections = sections.filter((s) => s.principle === principle);
      let earned = 0;
      let possible = 0;
      const levelOrder = { A: 1, AA: 2, AAA: 3 };

      principleSections.forEach((section) => {
        section.questions.forEach((q) => {
          if (levelOrder[q.level] > levelOrder[targetLevel]) return;
          const answer = answers[q.id];
          if (answer === 'na') return;
          possible += q.weight;
          if (answer === 'yes') earned += q.weight;
          else if (answer === 'partial') earned += q.weight * 0.5;
        });
      });

      const principleScore = possible > 0 ? Math.round((earned / possible) * 100) : 0;
      md += `| ${principle} | ${principleScore}% |\n`;
    });

    md += `\n## Section Scores\n\n`;
    md += `| Section | Score |\n`;
    md += `|---------|-------|\n`;
    sections.forEach((section) => {
      md += `| ${section.title} | ${getSectionScore(section.id)}% |\n`;
    });

    if (gaps.length > 0) {
      md += `\n## Accessibility Issues\n\n`;
      md += `### Level A Issues (Critical)\n\n`;
      const levelA = gaps.filter((g) => g.level === 'A');
      if (levelA.length > 0) {
        levelA.forEach((gap) => {
          md += `- **${gap.criterion}:** ${gap.question} (${gap.status})\n`;
        });
      } else {
        md += `No Level A issues identified.\n`;
      }

      md += `\n### Level AA Issues\n\n`;
      const levelAA = gaps.filter((g) => g.level === 'AA');
      if (levelAA.length > 0) {
        levelAA.forEach((gap) => {
          md += `- **${gap.criterion}:** ${gap.question} (${gap.status})\n`;
        });
      } else {
        md += `No Level AA issues identified.\n`;
      }

      if (targetLevel === 'AAA') {
        md += `\n### Level AAA Issues\n\n`;
        const levelAAA = gaps.filter((g) => g.level === 'AAA');
        if (levelAAA.length > 0) {
          levelAAA.forEach((gap) => {
            md += `- **${gap.criterion}:** ${gap.question} (${gap.status})\n`;
          });
        } else {
          md += `No Level AAA issues identified.\n`;
        }
      }
    }

    md += `\n## Legal Context\n\n`;
    md += `- **ADA (USA):** Courts increasingly reference WCAG 2.1 AA\n`;
    md += `- **Section 508 (USA):** Federal agencies must meet WCAG 2.0 AA\n`;
    md += `- **EAA (EU):** European Accessibility Act requires WCAG 2.1 AA by June 2025\n`;
    md += `- **AODA (Canada):** Ontario requires WCAG 2.0 AA for public sector\n`;

    md += `\n## Recommended Tools\n\n`;
    md += `- **Automated Testing:** axe DevTools, WAVE, Lighthouse\n`;
    md += `- **Screen Readers:** NVDA (Windows), VoiceOver (Mac/iOS), TalkBack (Android)\n`;
    md += `- **Color Contrast:** Colour Contrast Analyser, WebAIM Contrast Checker\n`;
    md += `- **Keyboard Testing:** Manual tab navigation testing\n`;

    md += `\n---\n*Assessment conducted using The Art of CTO WCAG 2.2 Compliance Checker*\n`;

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wcag-assessment-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const { score, gaps } = calculateScore();
    const compliance = getComplianceLevel(score);

    const gapItems = gaps.map((gap) => ({
      id: gap.criterion.toLowerCase().replace(/[^a-z0-9]/g, '-'),
      title: gap.question,
      severity: gap.level === 'A' ? 'critical' as const :
                gap.level === 'AA' ? 'high' as const : 'medium' as const,
      description: `${gap.status} - WCAG ${gap.criterion} (Level ${gap.level})`,
    }));

    const sectionScores: Record<string, number> = {};
    sections.forEach((section) => {
      sectionScores[section.id] = getSectionScore(section.id);
    });

    const recommendations = sections
      .filter((s) => getSectionScore(s.id) < 80)
      .sort((a, b) => getSectionScore(a.id) - getSectionScore(b.id))
      .map((section) => ({
        id: section.id,
        title: `Improve ${section.title}`,
        priority: getSectionScore(section.id) < 60 ? 'high' as const :
                  getSectionScore(section.id) < 80 ? 'high' as const : 'medium' as const,
        description: `Current score: ${getSectionScore(section.id)}%. Address ${section.principle} principle issues.`,
      }));

    const assessment = await saveResult({
      name: `WCAG 2.2 Level ${targetLevel} - ${new Date().toLocaleDateString()}`,
      score_overall: score,
      scores: sectionScores,
      answers,
      gaps: gapItems,
      recommendations,
      metadata: {
        conformanceLevel: compliance.level,
        targetLevel,
        totalQuestions: getApplicableQuestions().length,
        answeredQuestions: Object.keys(answers).length,
        levelAGaps: gaps.filter((g) => g.level === 'A').length,
        levelAAGaps: gaps.filter((g) => g.level === 'AA').length,
        levelAAAGaps: gaps.filter((g) => g.level === 'AAA').length,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link href="/tools" className="text-foreground-tertiary hover:text-accent mb-8 inline-block">
            ← Back to Tools
          </Link>

          <div className="text-center mb-12">
            <span className="text-accent text-sm font-medium tracking-wider uppercase">
              Web Accessibility
            </span>
            <h1 className="text-4xl font-bold text-foreground mt-2 mb-4">
              WCAG 2.2 Compliance Checker
            </h1>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              Assess your website or application against Web Content Accessibility Guidelines 2.2.
              Identify barriers and create an accessible digital experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">What is WCAG?</h3>
              <p className="text-foreground-secondary text-sm">
                The Web Content Accessibility Guidelines (WCAG) are international standards
                for making web content accessible to people with disabilities, including
                visual, auditory, motor, and cognitive impairments.
              </p>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">WCAG 2.2</h3>
              <p className="text-foreground-secondary text-sm">
                Released October 2023, WCAG 2.2 adds 9 new success criteria focused on
                cognitive accessibility, mobile interaction, and authentication.
                It builds on WCAG 2.0 and 2.1.
              </p>
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-4">Select Target Conformance Level</h3>
            <div className="grid grid-cols-3 gap-4">
              {(['A', 'AA', 'AAA'] as const).map((level) => (
                <button
                  key={level}
                  onClick={() => setTargetLevel(level)}
                  className={`p-4 rounded-lg border text-center transition-colors ${
                    targetLevel === level
                      ? 'bg-accent-muted border-accent text-accent'
                      : 'border-border text-foreground-secondary hover:border-border/40'
                  }`}
                >
                  <span className="text-2xl font-bold block mb-1">Level {level}</span>
                  <span className="text-xs">
                    {level === 'A' && 'Minimum'}
                    {level === 'AA' && 'Recommended'}
                    {level === 'AAA' && 'Enhanced'}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-foreground-tertiary text-sm mt-4">
              Level AA is the standard legal requirement in most jurisdictions.
            </p>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-3">POUR Principles</h3>
            <div className="space-y-3 text-sm text-foreground-secondary">
              <div className="flex gap-3">
                <span className="text-accent font-bold w-28">Perceivable</span>
                <span>Content must be presentable in ways users can perceive</span>
              </div>
              <div className="flex gap-3">
                <span className="text-accent font-bold w-28">Operable</span>
                <span>Interface components must be operable by all users</span>
              </div>
              <div className="flex gap-3">
                <span className="text-accent font-bold w-28">Understandable</span>
                <span>Content and operation must be understandable</span>
              </div>
              <div className="flex gap-3">
                <span className="text-accent font-bold w-28">Robust</span>
                <span>Content must work with current and future technologies</span>
              </div>
            </div>
          </div>

          <div className="bg-surface border border-border rounded-lg p-6 mb-8">
            <h3 className="text-accent font-semibold mb-2">Legal Requirements</h3>
            <p className="text-foreground-secondary text-sm">
              Many jurisdictions require WCAG compliance: ADA (USA), Section 508 (US federal),
              European Accessibility Act (EU by June 2025), and AODA (Ontario, Canada).
              Non-compliance can result in lawsuits and fines.
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={() => setView('assessment')}
              className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Start Accessibility Assessment
            </button>
            <p className="text-foreground-tertiary text-sm mt-4">
              Takes approximately 20-25 minutes to complete
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'assessment') {
    const section = sections[currentSection];
    const progress = ((currentSection + 1) / sections.length) * 100;
    const levelOrder = { A: 1, AA: 2, AAA: 3 };
    const applicableQuestions = section.questions.filter(
      (q) => levelOrder[q.level] <= levelOrder[targetLevel]
    );

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setView('landing')}
              className="text-foreground-tertiary hover:text-accent"
            >
              ← Back
            </button>
            <span className="text-foreground-tertiary text-sm">
              Section {currentSection + 1} of {sections.length}
            </span>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-foreground-tertiary mb-2">
              <span>{section.title}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-6">
            <span className="text-accent text-xs font-medium uppercase tracking-wider">
              {section.principle}
            </span>
            <h2 className="text-xl font-semibold text-foreground mt-1 mb-2">{section.title}</h2>
            <p className="text-foreground-tertiary">{section.description}</p>
          </div>

          <div className="space-y-4">
            {applicableQuestions.map((question) => (
              <div
                key={question.id}
                className="bg-surface border border-border-muted rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <div className="flex gap-2 items-center mb-1">
                      <span className="text-accent text-xs font-medium">
                        {question.criterion}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          question.level === 'A'
                            ? 'bg-red-500/20 text-red-400'
                            : question.level === 'AA'
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-green-500/20 text-green-400'
                        }`}
                      >
                        Level {question.level}
                      </span>
                    </div>
                    <p className="text-foreground">{question.text}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {(['yes', 'partial', 'no', 'na'] as const).map((value) => (
                    <button
                      key={value}
                      onClick={() => handleAnswer(question.id, value)}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        answers[question.id] === value
                          ? value === 'yes'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : value === 'partial'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                            : value === 'no'
                            ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/50'
                          : 'bg-cream/5 text-foreground-tertiary border border-border-muted hover:border-border/30'
                      }`}
                    >
                      {value === 'na' ? 'N/A' : value.charAt(0).toUpperCase() + value.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="px-6 py-2 rounded-lg border border-border text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:border-border/40 transition-colors"
            >
              Previous
            </button>
            {currentSection < sections.length - 1 ? (
              <button
                onClick={() => setCurrentSection(currentSection + 1)}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                Next Section
              </button>
            ) : (
              <button
                onClick={() => setView('results')}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                View Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { score, gaps } = calculateScore();
  const compliance = getComplianceLevel(score);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => setView('assessment')}
            className="text-foreground-tertiary hover:text-accent"
          >
            ← Back to Assessment
          </button>
          <button
            onClick={exportToMarkdown}
            className="bg-cream/10 hover:bg-cream/20 text-foreground px-4 py-2 rounded-lg transition-colors"
          >
            Export Report
          </button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            WCAG 2.2 Level {targetLevel} Results
          </h1>
          <div className={`inline-block px-6 py-3 rounded-lg ${compliance.bg}`}>
            <span className={`text-4xl font-bold ${compliance.color}`}>{score}%</span>
            <p className={`${compliance.color} font-medium mt-1`}>{compliance.level}</p>
          </div>
        </div>

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Section Scores</h2>
          <div className="space-y-3">
            {sections.map((section) => {
              const sectionScore = getSectionScore(section.id);
              const sectionCompliance = getComplianceLevel(sectionScore);
              return (
                <div key={section.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground-secondary">{section.title}</span>
                    <span className={sectionCompliance.color}>{sectionScore}%</span>
                  </div>
                  <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        sectionScore >= 95
                          ? 'bg-green-500'
                          : sectionScore >= 80
                          ? 'bg-yellow-500'
                          : sectionScore >= 60
                          ? 'bg-accent'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${sectionScore}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {gaps.length > 0 && (
          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Accessibility Issues ({gaps.length})
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {gaps.map((gap, index) => (
                <div
                  key={index}
                  className="border border-border-muted rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex gap-2 items-center mb-1">
                        <span className="text-accent text-xs font-medium">
                          {gap.criterion}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded ${
                            gap.level === 'A'
                              ? 'bg-red-500/20 text-red-400'
                              : gap.level === 'AA'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-green-500/20 text-green-400'
                          }`}
                        >
                          Level {gap.level}
                        </span>
                      </div>
                      <p className="text-foreground text-sm">{gap.question}</p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${
                        gap.status === 'Partial'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {gap.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-surface border border-border-muted rounded-lg p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Testing Tools</h2>
          <div className="text-foreground-secondary text-sm space-y-2">
            <p><span className="text-accent">Automated:</span> axe DevTools, WAVE, Lighthouse, Pa11y</p>
            <p><span className="text-accent">Screen Readers:</span> NVDA, VoiceOver, JAWS, TalkBack</p>
            <p><span className="text-accent">Color Contrast:</span> Colour Contrast Analyser, WebAIM</p>
            <p><span className="text-accent">Manual:</span> Keyboard-only navigation, zoom testing</p>
            <p className="mt-4 text-foreground-tertiary">
              Note: Automated tools catch only ~30-40% of accessibility issues.
              Manual testing and user testing are essential.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 justify-center mt-8">
          {commandCenterEnabled && !saved && (
            <button
              onClick={handleSaveToCommandCenter}
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save to Command Center'}
            </button>
          )}
          {saved && (
            <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Saved to Command Center
            </div>
          )}
          {saveError && (
            <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
              {saveError}
            </div>
          )}
          <button
            onClick={() => {
              setView('landing');
              setCurrentSection(0);
              setAnswers({});
              setSaved(false);
            }}
            className="px-6 py-3 border border-border rounded-lg text-foreground hover:border-accent transition-colors"
          >
            Start New Assessment
          </button>
        </div>
      </div>
    </div>
  );
}
